// Beckham Stack Pro upgrade — same sets the Rebuy widget 283094 callback used
// (assets/beckham-stack-rebuy-upgrade.js, retired with Rebuy). Lines whose
// product handle is in REPLACE are swapped out; anything in STACK means the
// shopper already owns the stack.
const CART_STACK_UPSELL_REPLACE_HANDLES = new Set([
  "essentials-pro",
  "essentials",
  "longevity",
  "daily-ultimate-longevity",
  "daily-ultimate-longevity-add-on",
]);

const CART_STACK_UPSELL_STACK_HANDLES = new Set([
  "the-beckham-stack-pro",
  "bstack-pro",
  "beckhamstack",
  "beckham-stack",
]);

class CartStackUpsell extends HTMLElement {
  constructor() {
    super();
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleAdd = this.handleAdd.bind(this);
    this.handleUpgrade = this.handleUpgrade.bind(this);
  }

  connectedCallback() {
    this.status = this.querySelector("[data-cart-stack-upsell-status]");
    this.upgradeButtons = Array.from(
      this.querySelectorAll("[data-cart-stack-upsell-upgrade]"),
    );
    this.upgradeButtons.forEach((button) =>
      button.addEventListener("click", this.handleUpgrade),
    );

    this.track = this.querySelector("[data-cart-stack-upsell-track]");
    this.slides = Array.from(
      this.querySelectorAll("[data-cart-stack-upsell-track] [data-cart-stack-upsell-slide]"),
    );
    this.buttons = Array.from(
      this.querySelectorAll("[data-cart-stack-upsell-add]:not([data-cart-stack-upsell-upgrade])"),
    );
    this.prevButton = this.querySelector("[data-cart-stack-upsell-prev]");
    this.nextButton = this.querySelector("[data-cart-stack-upsell-next]");
    this.controls = this.querySelector("[data-cart-stack-upsell-controls]");
    this.dots = this.querySelector("[data-cart-stack-upsell-dots]");

    if (!this.track || this.slides.length === 0) {
      this.controls?.classList.add("is-hidden");
      return;
    }

    this.buildDots();
    this.bindEvents();
    this.handleResize();
    this.handleScroll();
  }

  disconnectedCallback() {
    this.track?.removeEventListener("scroll", this.handleScroll);
    this.prevButton?.removeEventListener("click", this.handlePrev);
    this.nextButton?.removeEventListener("click", this.handleNext);
    this.buttons?.forEach((button) =>
      button.removeEventListener("click", this.handleAdd),
    );
    this.upgradeButtons?.forEach((button) =>
      button.removeEventListener("click", this.handleUpgrade),
    );
    window.removeEventListener("resize", this.handleResize);
  }

  bindEvents() {
    this.handlePrev = () => this.scrollByDirection(-1);
    this.handleNext = () => this.scrollByDirection(1);

    this.track.addEventListener("scroll", this.handleScroll, { passive: true });
    this.prevButton?.addEventListener("click", this.handlePrev);
    this.nextButton?.addEventListener("click", this.handleNext);
    this.buttons.forEach((button) =>
      button.addEventListener("click", this.handleAdd),
    );
    window.addEventListener("resize", this.handleResize);
  }

  buildDots() {
    if (!this.dots) return;

    this.dots.innerHTML = "";
    this.dotButtons = this.slides.map((slide, index) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "cart-stack-upsell__dot";
      dot.setAttribute("aria-label", `Go to recommendation ${index + 1}`);
      dot.addEventListener("click", () => this.scrollToSlide(index));
      this.dots.appendChild(dot);
      return dot;
    });
  }

  handleResize() {
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 1;
    this.classList.toggle("cart-stack-upsell--single", !hasOverflow);
    this.controls?.classList.toggle("is-hidden", !hasOverflow);
    this.handleScroll();
  }

  handleScroll() {
    if (!this.track || this.slides.length === 0) return;

    const trackLeft = this.track.getBoundingClientRect().left;
    const activeIndex = this.slides.reduce(
      (closest, slide, index) => {
        const distance = Math.abs(
          slide.getBoundingClientRect().left - trackLeft,
        );
        return distance < closest.distance ? { index, distance } : closest;
      },
      { index: 0, distance: Infinity },
    ).index;

    this.dotButtons?.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-current", isActive ? "true" : "false");
    });

    if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
    if (this.nextButton) {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      this.nextButton.disabled = this.track.scrollLeft >= maxScroll;
    }
  }

  scrollByDirection(direction) {
    const currentIndex = this.getActiveIndex();
    const nextIndex = Math.max(
      0,
      Math.min(this.slides.length - 1, currentIndex + direction),
    );
    this.scrollToSlide(nextIndex);
  }

  scrollToSlide(index) {
    const slide = this.slides[index];
    if (!slide || !this.track) return;
    // Scroll the track only. scrollIntoView would also nudge the drawer's
    // vertical scroll on mobile, which reads as a jump.
    const left =
      slide.getBoundingClientRect().left -
      this.track.getBoundingClientRect().left +
      this.track.scrollLeft;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    this.track.scrollTo({ left, behavior: reduce ? "auto" : "smooth" });
  }

  getActiveIndex() {
    return (
      this.dotButtons?.findIndex((dot) =>
        dot.classList.contains("is-active"),
      ) ?? 0
    );
  }

  async handleAdd(event) {
    const button = event.currentTarget;
    if (button.disabled || button.classList.contains("is-loading")) return;

    const variantId = button.getAttribute("data-variant-id");
    if (!variantId) return;

    const cart = document.querySelector("cart-drawer");
    const sections = cart
      ?.getSectionsToRender?.()
      .map((section) => section.id) || ["cart-drawer", "cart-icon-bubble"];
    const sellingPlanId = button.getAttribute("data-selling-plan-id");
    const item = {
      id: variantId,
      quantity: 1,
      ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
    };

    this.setButtonState(button, true);
    this.setStatus("Adding recommendation to cart");

    try {
      // IM8Cart (global.js): Standard Actions first, guarded Ajax second.
      // Resolves to the /cart/add.js contract; rejections carry `.status`.
      const parsedState = await window.IM8Cart.add([item], { sections, context: "cart" });

      if (!parsedState || parsedState.status) {
        throw new Error((parsedState && parsedState.description) || "Unable to add item");
      }

      if (parsedState.sections && Object.keys(parsedState.sections).length) {
        cart?.renderContents?.(parsedState);
      }
      this.setStatus("Added to cart");
    } catch (error) {
      console.error("Error adding cart stack upsell:", error);
      if (window.IM8CartGuard && error && (error.cartBlocked || error.name === "AbortError")) {
        // Bot-protection challenge / hung edge: hand the add to a GET
        // permalink so the user clears the challenge once.
        window.IM8CartGuard.recoverViaNavigation(item, { returnTo: "/cart" });
        return;
      }
      this.setStatus("Unable to add item. Please try again.");
      this.setButtonState(button, false);
    }
  }

  // ─── Beckham Stack Pro upgrade ──────────────────────────────────────
  // Port of the Rebuy Smart Cart widget 283094 flow (retired with Rebuy):
  //   fetch cart → pick stack variant (flavor + cadence) → add stack →
  //   remove every replaceable line → sync welcome gifts → repaint.
  // Liquid already resolved the variant/plan/quantity into data-*; here the
  // add + removes collapse into ONE IM8Cart.replace mutation (Storefront
  // cartLines mutation, guarded Ajax fallback) and syncAllGifts reconciles
  // welcome gifts — stack gifts in, essentials/longevity gifts out.

  static itemHandle(item) {
    return String(
      (item && (item.handle || item.product_handle || (item.product && item.product.handle))) || "",
    ).toLowerCase();
  }

  static isStackItem(item) {
    return CART_STACK_UPSELL_STACK_HANDLES.has(CartStackUpsell.itemHandle(item));
  }

  static isReplaceable(item) {
    if (!item || CartStackUpsell.isStackItem(item)) return false;
    return CART_STACK_UPSELL_REPLACE_HANDLES.has(CartStackUpsell.itemHandle(item));
  }

  setUpgradeInFlight(active) {
    // Honoured by IM8ScheduleGiftSync / the Rebuy shim: no gift sync may
    // race the swap mid-flight.
    window.__im8BeckhamStackUpgradeInFlight = !!active;
    document.body.classList.toggle("im8-beckham-stack-upgrading", !!active);
  }

  async handleUpgrade(event) {
    const button = event.currentTarget;
    if (button.disabled || button.classList.contains("is-loading")) return;
    if (document.documentElement.dataset.cartUpdating === "true") return;

    const variantId = button.getAttribute("data-variant-id");
    const sellingPlanId = button.getAttribute("data-selling-plan-id");
    const quantity = Math.max(1, parseInt(button.getAttribute("data-quantity"), 10) || 1);
    const renderedKeys = (button.getAttribute("data-replace-keys") || "")
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    if (!variantId || !sellingPlanId) return;

    const cart = document.querySelector("cart-drawer");
    const sections = cart
      ?.getSectionsToRender?.()
      .map((section) => section.id) || ["cart-drawer", "cart-icon-bubble"];
    const stackItem = {
      id: Number(variantId),
      quantity,
      selling_plan: Number(sellingPlanId),
    };

    const canLock = typeof window.toggleCartState === "function";
    this.setButtonState(button, true);
    this.setStatus("Upgrading to the Beckham Stack");
    if (canLock) window.toggleCartState(true);
    this.setUpgradeInFlight(true);

    try {
      // Rebuy started from a fresh cart read; so do we. Line keys rendered
      // into the slide can go stale (qty edits, another tab), so resolve
      // the removals against live cart JSON and only fall back to the
      // rendered keys when the read is refused.
      let removeRefs = renderedKeys.map((key) => ({ key }));
      let liveCart = null;
      try {
        liveCart = await window.IM8Cart.get();
      } catch (readError) {
        console.warn("[Cart Stack Upsell] cart read failed, using rendered keys:", readError && readError.message);
      }
      if (liveCart && Array.isArray(liveCart.items)) {
        removeRefs = liveCart.items
          .filter(CartStackUpsell.isReplaceable)
          .map((item) => ({ key: item.key }));
      }
      const alreadyHasStack = !!(liveCart && Array.isArray(liveCart.items) && liveCart.items.some(CartStackUpsell.isStackItem));
      const adds = alreadyHasStack ? [] : [stackItem];

      const parsedState = await window.IM8Cart.replace(removeRefs, adds, {
        sections,
        context: "cart",
        cart: liveCart || undefined,
        skipGiftSync: true,
      });

      if (!parsedState || (parsedState.status && parsedState.status >= 400)) {
        throw new Error(
          (parsedState && (parsedState.description || parsedState.message)) || "Unable to upgrade",
        );
      }
      // Rebuy re-read the cart to confirm the stack landed. Our replace()
      // already returns the post-mutation cart (or the add.js response on
      // the Ajax tier); only judge it when we actually got items back.
      if (!alreadyHasStack && !parsedState.partial && Array.isArray(parsedState.items) && parsedState.items.length) {
        const landed = parsedState.items.some(
          (item) => String(item.variant_id || item.id) === String(variantId),
        );
        if (!landed) throw new Error("Beckham Stack was not added");
      }

      // Paint the swapped cart right away; gift reconciliation repaints
      // again once the welcome gifts settle.
      if (parsedState.sections && Object.keys(parsedState.sections).length) {
        cart?.renderContents?.(parsedState);
      }

      this.setUpgradeInFlight(false);
      if (typeof window.syncAllGifts === "function") {
        await window.syncAllGifts();
      }

      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-stack-upsell-upgrade",
          productVariantId: variantId,
          cartData: parsedState,
          eventType: typeof EVENT_TYPE !== "undefined" && EVENT_TYPE.add ? EVENT_TYPE.add : "add",
          giftsIncluded: true,
        });
      }

      console.log("[Cart Stack Upsell] Beckham Stack upgrade complete", {
        variantId,
        sellingPlanId,
        quantity,
        removed: removeRefs.length,
        transport: parsedState.transport,
      });
      this.setStatus("Upgraded to the Beckham Stack");
    } catch (error) {
      console.error("[Cart Stack Upsell] Beckham Stack upgrade failed:", error);
      // No GET-permalink recovery here: a permalink can add the stack but
      // cannot remove the lines it replaces, so the shopper would end up
      // with both. Surface the failure and let them retry.
      this.setStatus(error && error.cartOutcomeUnknown ? error.message : "Unable to upgrade. Please try again.");
      this.setButtonState(button, false);
    } finally {
      this.setUpgradeInFlight(false);
      if (canLock) window.toggleCartState(false);
    }
  }

  setButtonState(button, isLoading) {
    button.disabled = isLoading;
    button.classList.toggle("is-loading", isLoading);
    button.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get("cart-stack-upsell")) {
  customElements.define("cart-stack-upsell", CartStackUpsell);
}
